<template>
  <div class="audit-wrapper">
    <h1 class="mb-4">
      Audit Trail
    </h1>

    <div
      v-if="errorMessage"
      class="alert alert-danger"
    >
      {{ errorMessage }}
    </div>

    <LoadingSpinner v-if="loading" />

    <template v-else>
      <table class="audit-table w-100">
        <thead>
          <tr>
            <th class="col-date">
              Date
            </th>
            <th>Activity</th>
            <th class="col-user">
              User
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!items.length">
            <td
              colspan="3"
              class="text-center text-muted py-4"
            >
              No audit records found
            </td>
          </tr>
          <tr
            v-for="item in items"
            :key="item.id"
            class="audit-row"
          >
            <td class="col-date">
              {{ formatDate(item.event_time) }}
            </td>
            <td>
              <div class="fw-medium">
                {{ item.description }}
              </div>
              <div class="text-muted small mt-1">
                Task: {{ item.task_name }}
              </div>
            </td>
            <td class="col-user">
              {{ item.username || '—' }}
            </td>
          </tr>
        </tbody>
      </table>

      <div class="d-flex justify-content-end align-items-center gap-2 mt-3">
        <button
          class="page-btn"
          :disabled="page === 0"
          @click="changePage(page - 1)"
        >
          &lt;
        </button>
        <select
          :value="page + 1"
          @change="onSelectChange"
        >
          <option
            v-for="p in totalPages"
            :key="p"
            :value="p"
          >
            {{ p }}
          </option>
        </select>
        <span class="text-muted small">/ {{ totalPages }}</span>
        <button
          class="page-btn"
          :disabled="page >= totalPages - 1"
          @click="changePage(page + 1)"
        >
          &gt;
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useApi } from '@/composables/useAppGlobals';
import { useTime } from '@/composables/useTime';
import LoadingSpinner from './components/widgets/LoadingSpinner.vue';
import type { AuditLogItem } from '@/types/api';

const $api = useApi();
const { dateTimeFormat } = useTime();

const items = ref<AuditLogItem[]>([]);
const page = ref(0);
const totalPages = ref(1);
const loading = ref(false);
const errorMessage = ref<string | null>(null);

const formatDate = (dt: string) => dateTimeFormat(dt);

const load = async () => {
  loading.value = true;
  errorMessage.value = null;
  try {
    const resp = await $api.getAuditTrail(page.value);
    items.value = resp.items;
    totalPages.value = resp.total_pages || 1;
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    errorMessage.value = status ? `Error ${status}` : 'Failed to load audit trail';
  } finally {
    loading.value = false;
  }
};

const changePage = async (newPage: number) => {
  page.value = newPage;
  await load();
};

const onSelectChange = async (e: Event) => {
  await changePage(Number((e.target as HTMLSelectElement).value) - 1);
};

onMounted(load);
</script>

<style lang="scss" scoped>
.audit-wrapper {
  margin: 60px 9%;
}

.audit-table {
  border-collapse: collapse;

  th {
    padding: 8px 12px;
    font-weight: 500;
    color: #333;
    border-bottom: 1px solid #e8e8e8;
  }

  .audit-row td {
    padding: 14px 12px;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: top;
    font-size: 14px;
    color: #555;
  }
}

.col-date { width: 160px; white-space: nowrap; }
.col-user { width: 200px; }

.page-btn {
  background: none;
  border: none;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  color: #333;

  &:disabled { color: #ccc; cursor: default; }
  &:not(:disabled):hover { color: #4a90d9; }
}
</style>
